import { useState, useCallback, useRef } from 'react';
import clinicalService from 'portal/services/clinicalService';

// ─────────────────────────────────────────────────────────────────────────────
// In-memory cache (same pattern as usePredictive.js)
// ─────────────────────────────────────────────────────────────────────────────
const cache = {};
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function getCached(key) {
  const entry = cache[key];
  if (entry && Date.now() - entry.timestamp < CACHE_TTL) return entry.data;
  return null;
}

function setCache(key, data) {
  cache[key] = { data, timestamp: Date.now() };
}

// Wipe every entry — called on login/logout/impersonation (see store.js) so a
// prior session's clinical data can never leak into the next one via this
// module-scope cache, which outlives React/Redux state.
export function clearClinicalCache() {
  Object.keys(cache).forEach((key) => delete cache[key]);
}

// ─────────────────────────────────────────────────────────────────────────────
// Generic fetcher
// ─────────────────────────────────────────────────────────────────────────────
function useClinicalFetcher(cacheKey) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);

  const execute = useCallback(
    async (serviceFn, ...args) => {
      const cached = getCached(cacheKey);
      if (cached) {
        setData(cached);
        return cached;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await serviceFn(...args);
        if (response?.rescode === 1) {
          setData(response.data);
          setCache(cacheKey, response.data);
          return response.data;
        } else {
          const msg = response?.message || 'Failed to fetch clinical data';
          setError(msg);
        }
        return null;
      } catch (err) {
        setError(err?.message || 'Something went wrong');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [cacheKey]
  );

  const reset = useCallback(() => {
    fetchedRef.current = false;
    delete cache[cacheKey];
  }, [cacheKey]);

  return { data, loading, error, execute, fetchedRef, reset };
}

// ─────────────────────────────────────────────────────────────────────────────
// usePatientProgress — All patients progress scores
// ─────────────────────────────────────────────────────────────────────────────
export function usePatientProgress() {
  const { data, loading, error, execute, fetchedRef } = useClinicalFetcher('clinical-progress');

  const fetch = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    return execute(clinicalService.getPatientProgress);
  }, [execute, fetchedRef]);

  return { data, loading, error, fetch };
}

// ─────────────────────────────────────────────────────────────────────────────
// useSinglePatientProgress — Single patient progress
// ─────────────────────────────────────────────────────────────────────────────
export function useSinglePatientProgress(patientId) {
  const { data, loading, error, execute, fetchedRef } = useClinicalFetcher(`clinical-progress-${patientId}`);

  const fetch = useCallback(async () => {
    if (fetchedRef.current || !patientId) return;
    fetchedRef.current = true;
    return execute(clinicalService.getSinglePatientProgress, patientId);
  }, [execute, fetchedRef, patientId]);

  return { data, loading, error, fetch };
}

// ─────────────────────────────────────────────────────────────────────────────
// useTherapyEffectiveness — Therapy effectiveness rankings
// ─────────────────────────────────────────────────────────────────────────────
export function useTherapyEffectiveness() {
  const { data, loading, error, execute, fetchedRef } = useClinicalFetcher('therapy-effectiveness');

  const fetch = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    return execute(clinicalService.getTherapyEffectiveness);
  }, [execute, fetchedRef]);

  return { data, loading, error, fetch };
}

// ─────────────────────────────────────────────────────────────────────────────
// useGoalAchievement — All patients goal tracking
// ─────────────────────────────────────────────────────────────────────────────
export function useGoalAchievement() {
  const { data, loading, error, execute, fetchedRef } = useClinicalFetcher('clinical-goals');

  const fetch = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    return execute(clinicalService.getGoalAchievement);
  }, [execute, fetchedRef]);

  return { data, loading, error, fetch };
}

// ─────────────────────────────────────────────────────────────────────────────
// useSinglePatientGoals — Single patient goals
// ─────────────────────────────────────────────────────────────────────────────
export function useSinglePatientGoals(patientId) {
  const { data, loading, error, execute, fetchedRef } = useClinicalFetcher(`clinical-goals-${patientId}`);

  const fetch = useCallback(async () => {
    if (fetchedRef.current || !patientId) return;
    fetchedRef.current = true;
    return execute(clinicalService.getSinglePatientGoals, patientId);
  }, [execute, fetchedRef, patientId]);

  return { data, loading, error, fetch };
}

// ─────────────────────────────────────────────────────────────────────────────
// useStagnationAlerts — Stagnation detection
// ─────────────────────────────────────────────────────────────────────────────
export function useStagnationAlerts() {
  const { data, loading, error, execute, fetchedRef } = useClinicalFetcher('clinical-stagnation');

  const fetch = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    return execute(clinicalService.getStagnationAlerts);
  }, [execute, fetchedRef]);

  return { data, loading, error, fetch };
}

// ─────────────────────────────────────────────────────────────────────────────
// useClinicalRecommendations — All patients recommendations
// ─────────────────────────────────────────────────────────────────────────────
export function useClinicalRecommendations() {
  const { data, loading, error, execute, fetchedRef } = useClinicalFetcher('clinical-recommendations');

  const fetch = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    return execute(clinicalService.getAllRecommendations);
  }, [execute, fetchedRef]);

  return { data, loading, error, fetch };
}

// ─────────────────────────────────────────────────────────────────────────────
// useSinglePatientRecommendations — Single patient recommendations
// ─────────────────────────────────────────────────────────────────────────────
export function useSinglePatientRecommendations(patientId) {
  const { data, loading, error, execute, fetchedRef } = useClinicalFetcher(`clinical-recs-${patientId}`);

  const fetch = useCallback(async () => {
    if (fetchedRef.current || !patientId) return;
    fetchedRef.current = true;
    return execute(clinicalService.getPatientRecommendations, patientId);
  }, [execute, fetchedRef, patientId]);

  return { data, loading, error, fetch };
}
